import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Menu,
  X,
  User,
  LogOut,
  Settings,
  Calendar,
  Shield,
  Home,
  Search,
  ListOrdered,
  Info,
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const ClientHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const sheetRef = useRef(null);

  // Check if we're on pages that use transparent header (dark hero below)
  const isHomePage = location.pathname === '/';
  const isAboutPage = location.pathname === '/about';
  const isHelpCenterPage = location.pathname === '/help';
  const isContactPage = location.pathname === '/contact';
  const isSafetyPage = location.pathname === '/safety';
  const isVendorSuccessPage = location.pathname === '/vendor/success';
  const isTermsPage = location.pathname === '/terms';
  const isPrivacyPage = location.pathname === '/privacy';

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  };

  const closeMenu = () => setIsMenuOpen(false);

  // Handle scroll detection for transparent/black header
  useEffect(() => {
    let rafId = null;
    let ticking = false;

    const checkScroll = () => {
      setIsScrolled(window.scrollY > 50);
      ticking = false;
    };

    checkScroll();

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(checkScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll + Escape to close when mobile menu is open
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Browse Vendors', href: '/browse-vendors', icon: Search },
    { name: 'How It Works', href: '/how-it-works', icon: ListOrdered },
    { name: 'About', href: '/about', icon: Info },
  ];

  const shouldBeTransparent =
    (isHomePage ||
      isAboutPage ||
      isHelpCenterPage ||
      isContactPage ||
      isSafetyPage ||
      isVendorSuccessPage ||
      isTermsPage ||
      isPrivacyPage) &&
    !isScrolled;

  const isAdmin = user?.user_type === 'admin';
  const isVendor = user?.user_type === 'vendor';

  const profileHref = isAdmin
    ? '/admin'
    : isVendor
      ? '/vendor'
      : '/bookings';

  const dashboardHref = isAdmin ? '/admin' : isVendor ? '/vendor' : '/dashboard';
  const dashboardLabel = isAdmin ? 'Go to Admin' : isVendor ? 'Vendor Dashboard' : 'My Bookings';
  const DashboardIcon = isAdmin ? Shield : Calendar;

  const settingsHref = isAdmin
    ? '/admin/settings'
    : isVendor
      ? '/vendor/profile'
      : '/settings';

  const mobileMenu =
    isMenuOpen &&
    createPortal(
      <div className="md:hidden fixed inset-0 dashboard-surface z-[11000] flex flex-col justify-end" role="dialog" aria-modal="true">
        {/* Backdrop */}
        <button
          type="button"
          aria-label="Close menu"
          className="absolute inset-0 bg-black/55 backdrop-blur-[2px] animate-fade-in"
          onClick={closeMenu}
        />

        {/* Bottom sheet */}
        <div
          ref={sheetRef}
          className="relative w-full max-h-[88vh] bg-black text-white rounded-t-3xl shadow-2xl animate-slideUp overflow-hidden flex flex-col"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          {/* Drag handle + close */}
          <div className="flex items-center justify-between px-5 pt-3 pb-2 shrink-0">
            <div className="w-10" />
            <div className="w-10 h-1 rounded-full bg-white/30" aria-hidden="true" />
            <button
              type="button"
              onClick={closeMenu}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="overflow-y-auto overscroll-contain px-5 pb-2">
            {/* Brand */}
            <div className="flex items-center gap-3 mb-5">
              <img src="/images/VendaLOGO1.png" alt="VendaList" className="h-8 w-auto" />
            </div>

            {/* Navigation */}
            <nav className="space-y-1.5 mb-5">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeMenu}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-medium transition-colors ${
                      isActive
                        ? 'bg-white text-black'
                        : 'text-white/90 hover:bg-white/10 active:bg-white/15'
                    }`}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-black' : 'text-white/70'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="h-px bg-white/15 mb-5" />

            {/* Auth / account */}
            {isAuthenticated ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-2xl bg-white/5 p-1.5">
                  <Link
                    to={profileHref}
                    onClick={closeMenu}
                    className="flex min-w-0 flex-1 items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/10 active:bg-white/15 transition-colors"
                    aria-label={
                      isAdmin || isVendor ? 'Open dashboard' : 'Open bookings'
                    }
                  >
                    <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-white/55 truncate mt-0.5">{user?.email}</p>
                    </div>
                  </Link>
                  <div className="shrink-0 pr-1" onClick={(e) => e.stopPropagation()}>
                    <NotificationBell />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Link
                    to={dashboardHref}
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-medium text-white/90 hover:bg-white/10 transition-colors"
                  >
                    <DashboardIcon className="w-5 h-5 text-white/70" />
                    {dashboardLabel}
                  </Link>
                  <Link
                    to={settingsHref}
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-medium text-white/90 hover:bg-white/10 transition-colors"
                  >
                    <Settings className="w-5 h-5 text-white/70" />
                    Profile Settings
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-medium text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pb-1">
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="block w-full text-center text-[15px] font-medium text-white py-3.5 rounded-2xl border border-white/25 hover:bg-white/10 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={closeMenu}
                  className="block w-full text-center text-[15px] font-semibold text-black bg-white py-3.5 rounded-2xl hover:bg-gray-100 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <header
      className={`sticky top-0 z-[10000] transition-all duration-300 ${
        shouldBeTransparent ? '' : 'bg-black shadow-lg border-b border-black'
      }`}
      style={shouldBeTransparent ? { backgroundColor: 'transparent', backdropFilter: 'none' } : {}}
    >
      <div className="page-container">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/images/VendaLOGO1.png" alt="Logo" className="w-36 h-12" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-light transition-colors ${
                  shouldBeTransparent
                    ? 'text-white hover:text-white drop-shadow-md'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated && <NotificationBell className="mr-2" />}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center space-x-2 transition-colors ${
                    shouldBeTransparent
                      ? 'text-white hover:text-white drop-shadow-md'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      shouldBeTransparent ? 'bg-white/20 ' : 'bg-white'
                    }`}
                  >
                    <User className={`w-4 h-4 ${shouldBeTransparent ? 'text-white' : 'text-black'}`} />
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-[10001] border border-gray-200">
                    <Link
                      to={profileHref}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block px-4 py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-black">{user?.name || 'User'}</div>
                      <div className="text-xs text-gray-600 mt-1 truncate">{user?.email}</div>
                    </Link>

                    {isAdmin ? (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Shield className="w-4 h-4 mr-3" />
                        Go to Admin
                      </Link>
                    ) : isVendor ? (
                      <>
                        <Link
                          to="/vendor"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="w-4 h-4 mr-3" />
                          Vendor Dashboard
                        </Link>
                        <Link
                          to="/vendor/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 mr-3" />
                          Profile Settings
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="w-4 h-4 mr-3" />
                          My Bookings
                        </Link>
                        <Link
                          to="/settings"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 mr-3" />
                          Profile Settings
                        </Link>
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    shouldBeTransparent
                      ? 'text-white hover:text-white drop-shadow-md'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className={`btn-primary px-3 text-sm font-light transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 ${
                    shouldBeTransparent
                      ? 'bg-white text-black hover:bg-gray-50 shadow-lg'
                      : 'bg-white text-black hover:bg-gray-50 shadow-md hover:shadow-lg'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            className={`md:hidden p-2 rounded-md transition-colors ${
              shouldBeTransparent
                ? 'text-white hover:bg-white/10 drop-shadow-md'
                : 'text-gray-300 hover:text-white hover:bg-gray-800'
            }`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenu}
    </header>
  );
};

export default ClientHeader;
